import { useEffect, useState } from "react";
import { api, type SmsMessage } from "../api";
import { formatDue } from "../scheduler/dates";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";

const FIELD = "w-full rounded-[14px] bg-white px-4 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20";
const LAST_NUMBER_KEY = "smsLastNumber";
const DEFAULT_NUMBER = "+47 41261013";
const DEFAULT_TEXT = "Test from SoulCake Status App nr. 001";

const STATUS_STYLE: Record<string, string> = {
  delivered: "bg-lavender",
  read: "bg-lavender",
  failed: "bg-danger text-white",
  undelivered: "bg-danger text-white",
};

export function SmsHistory({ onBack, embedded = false }: { onBack: () => void; embedded?: boolean }) {
  const [rows, setRows] = useState<SmsMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .smsHistory()
      .then(setRows)
      .catch(() => setError("Couldn't load history."));
  }, []);

  return (
    <div className={embedded ? "flex flex-1 flex-col gap-5" : "mx-auto flex min-h-dvh max-w-md flex-col gap-5 px-4 pb-6 pt-4"}>
      <BackHeader title="SMS log" onBack={onBack} />
      {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
      {!!rows?.length && (
        <p className="px-1 text-xs font-semibold text-ink/50">
          {rows.length} messages · {rows.reduce((sum, m) => sum + m.costNok, 0).toFixed(2)} NOK
          {rows.some((m) => m.costEstimated) && " (~ = estimated until Twilio reports the price)"}
        </p>
      )}
      {rows === null && !error && <p className="py-8 text-center font-semibold text-ink/50">Loading…</p>}
      {rows?.length === 0 && <p className="py-8 text-center font-semibold text-ink/50">No messages sent yet.</p>}
      {!!rows?.length && (
        <ul className="flex flex-col gap-2">
          {rows.map((m) => (
            <li key={m.id} className="flex flex-col gap-1 rounded-[14px] bg-white px-4 py-3 border border-line-soft shadow-card">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{m.to}</span>
                <span className="flex items-center gap-2">
                  {m.costNok > 0 && (
                    <span className="text-xs font-bold text-ink/60" title={m.costEstimated ? "Estimated until Twilio reports the price" : "Twilio price"}>
                      {m.costEstimated ? "~" : ""}
                      {m.costNok.toFixed(2)} NOK
                    </span>
                  )}
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLE[m.status] ?? "bg-lemon"}`}>
                    {m.status}
                  </span>
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm font-semibold text-ink/70">{m.body}</p>
              <div className="text-xs font-semibold text-ink/50">
                {formatDue(m.createdAt)} · from {m.sender}
                {m.segments && m.segments > 1 && <span> · {m.segments} parts</span>}
                {m.taskId && <span> · task #{m.taskId}</span>}
                {m.error && <span className="text-danger"> · {m.error}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Hidden page at /sms: send a text message through Twilio. */
export default function SmsScreen({ onBack }: { onBack: () => void }) {
  const [showHistory, setShowHistory] = useState(false);
  const [to, setTo] = useState(() => localStorage.getItem(LAST_NUMBER_KEY) ?? DEFAULT_NUMBER);
  const [text, setText] = useState(DEFAULT_TEXT);
  const [askPin, setAskPin] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!result?.ok) return;
    const t = setTimeout(() => setResult(null), 8000);
    return () => clearTimeout(t);
  }, [result]);

  const ready = to.trim() !== "" && text.trim() !== "";

  const send = async (pin: string) => {
    const res = await api.sendSms(to.trim(), text.trim(), pin);
    localStorage.setItem(LAST_NUMBER_KEY, res.to);
    setTo(res.to);
    setText("");
    setAskPin(false);
    setResult({ ok: true, message: `Sent to ${res.to}` });
  };

  if (showHistory) return <SmsHistory onBack={() => setShowHistory(false)} />;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 px-4 pb-6 pt-4">
      <BackHeader
        title="Send SMS"
        onBack={onBack}
        action={
          <button
            onClick={() => setShowHistory(true)}
            className="shrink-0 flex h-10 items-center rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] active:scale-95"
          >
            History
          </button>
        }
      />

      <form
        className="pop-in flex flex-1 flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) setAskPin(true);
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Number</span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={DEFAULT_NUMBER}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={`${FIELD} h-14`}
          />
        </label>

        <label className="flex flex-1 flex-col gap-1.5">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Text</span>
          <textarea
            rows={6}
            maxLength={1000}
            placeholder="Type text to send"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`${FIELD} resize-none py-3`}
          />
          <span className="px-1 text-right text-xs font-semibold text-ink/40">{text.length} / 1000</span>
        </label>

        {result && (
          <p className={`text-center text-sm font-semibold ${result.ok ? "text-ink/70" : "text-hot"}`}>
            {result.ok ? "✓ " : ""}
            {result.message}
          </p>
        )}

        <button
          type="submit"
          disabled={!ready}
          className="mt-auto h-16 w-full rounded-[14px] bg-rose-grad text-lg font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-50 shadow-button"
        >
          Send
        </button>
      </form>

      {askPin && (
        <PinDialog
          title="Send SMS?"
          detail={`To ${to.trim()}. Enter the PIN to send.`}
          confirmLabel="Send"
          confirmBusyLabel="Sending…"
          confirmClass="bg-rose-grad text-white shadow-button"
          onConfirm={send}
          onCancel={() => setAskPin(false)}
        />
      )}
    </div>
  );
}
